import React from 'react';
import styled, { css } from 'styled-components';
import { withI18n } from 'react-i18next';
import Typography from './Typography';
import AddIcon from 'images/add.svg';
import AddIconWhite from 'images/add_white.svg';
import DropdownIcon from 'images/double_caret_vertical.svg';
import ClearCircleIcon from 'images/clear_circle.svg';

// type CustomChangeEvent = {
//   target: {
//     name: string;
//     value: any;
//   };
// };

// type DropdownOption = {
//   label: string;
//   value: any;
// };

// type Props = {
//   id?: string;
//   className?: string;
//   style?: any;
//   addOptionText?: string;
//   allowAdd?: boolean;
//   error?: boolean;
//   errorMessage?: string;
//   isLoading?: boolean;
//   label?: string;
//   name?: string;
//   options: DropdownOption[];
//   placeholder?: string;
//   ref?: any;
//   value: DropdownOption;
//   onAdd?: () => void;
//   onBlur?: (event: CustomChangeEvent) => void;
//   onSelect: (event: CustomChangeEvent) => void;
// };

class Select extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      imagesToPreload: [AddIcon, AddIconWhite],
      showMenu: false
    };
  }

  componentDidMount() {
    // force the component to load images into memory early
    this.state.imagesToPreload.forEach((imgSrc) => {
      const img = new Image();
      img.src = imgSrc;
    });
  }

  handleBlur = () => {
    this.setState({ showMenu: false });
    this.props.onBlur &&
      this.props.onBlur({
        target: { name, value }
      });
  };

  handleClick = () => {
    this.setState({ showMenu: !this.state.showMenu });
  };

  handleClickOption = (item) => {
    this.props.onSelect({
      target: {
        name: this.props.name,
        value: item
      }
    });
    this.setState({ showMenu: false });
  };

  render() {
    const {
      t,
      id,
      className,
      style,
      addOptionText,
      allowAdd,
      error,
      errorMessage,
      innerRef,
      isLoading,
      label,
      name,
      options,
      placeholder,
      value,
      onAdd: handleAdd,
      onBlur,
      onSelect
    } = this.props;
    const { showMenu } = this.state;

    const props = { id, className, style };

    const Select = (
      <StyledButton
        innerRef={innerRef}
        error={error}
        hasValue={Boolean(value && value.value)}
        type="button"
        onBlur={this.handleBlur}
        onClick={this.handleClick}
      >
        <StyledSpan title={value ? value.label : null}>
          {value ? value.label : placeholder}
        </StyledSpan>
        <StyledDropdownIcon src={DropdownIcon} />
      </StyledButton>
    );

    const SelectMenu = showMenu && (
      <Menu hasLabel={label}>
        {isLoading ? (
          <StyledLi>{t('Loading...')}</StyledLi>
        ) : (
          <>
            {options.length ? (
              options.map((item) => (
                <MenuItem
                  key={item.value}
                  onClick={() => this.handleClickOption(item)}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  {item.label}
                </MenuItem>
              ))
            ) : (
              <StyledLi>{t('No options')}</StyledLi>
            )}
            {allowAdd && (
              <StyledMenuItem
                onClick={handleAdd}
                onMouseDown={(e) => e.preventDefault()}
              >
                <Typography component="span" variant="body">
                  {addOptionText ? addOptionText : t('Add option')}
                </Typography>
              </StyledMenuItem>
            )}
          </>
        )}
      </Menu>
    );

    return (
      <StyledDiv {...props}>
        {label ? (
          <StyledLabel>
            {label}
            {Select}
          </StyledLabel>
        ) : (
          Select
        )}
        {SelectMenu}
        {error && errorMessage && (
          <ErrorContainer>
            <StyledImg src={ClearCircleIcon} />
            <Typography color="error" variant="captionBold">
              {errorMessage}
            </Typography>
          </ErrorContainer>
        )}
      </StyledDiv>
    );
  }
}

const StyledDiv = styled.div`
  position: relative;
`;

const StyledLabel = styled.label`
  width: 100%;
  color: #364866;
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 21px;

  & > button {
    margin-top: 8px;
  }
`;

const StyledButton = styled.button`
  width: 100%;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  color: ${(p) => p.hasValue ? '#364866' : '#8A94A6'};
  background-color: #FFFFFF;
  border: 1px solid #D1D5DD;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  text-align: left;

  &:focus {
    border-color: #0F9BF0;
    outline: none;
  }

  ${(p) =>
    p.error &&
    css`
      border-color: #E5084A;
    `}
`;

const StyledSpan = styled.span`
  color: inherit;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledDropdownIcon = styled.img`
  width: 24px;
  height: 24px;
  margin-right: -8px;
`;

const Menu = styled.ul`
  width: 100%;
  max-height: 280px;
  position: absolute;
  top: ${p => p.hasLabel ? '68px' : '40px'};
  left: 0;
  padding: 0;
  background-color: #FFFFFF;
  border: 1px solid #D1D5DD;
  border-radius: 4px;
  list-style: none;
  overflow-y: auto;
  z-index: 100;
`;

const baseLiStyles = css`
  padding: 4px 16px;
  font-size: 14px;
  font-weight: 400;
  line-height: 19px;

  &:first-of-type {
    margin-top: 8px;
  }

  &:last-of-type {
    margin-bottom: 8px;
  }
`;

const MenuItem = styled.li`
  ${baseLiStyles}

  color: #364866;
  cursor: pointer;

  &:focus,
  &:hover {
    color: #FFFFFF;
    background-color: #0F9BF0;
  }
`;

const StyledLi = styled.li`
  ${baseLiStyles}

  color: #8A94A6;
  cursor: default;
`;

const StyledMenuItem = styled(MenuItem)`
  & > span {
    color: #0F9BF0;
  }

  &:hover > span {
    color: #FFFFFF;
  }

  & > span::before {
    position: relative;
    top: 3px;
    margin-right: 4px;
    content: url(${AddIcon});
  }

  &:hover > span::before {
    content: url(${AddIconWhite});
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 8px;
`;

const StyledImg = styled.img`
  margin: 0 4px 2px 0;
`;

export default withI18n()(
  React.forwardRef((props, ref) => <Select innerRef={ref} {...props} />)
);
